import i18next from "i18next";
import { IAxiosGetResponse, IBaseConditionNode, IClaimAutocompleteResultDto, IClaimConditionNodeDto, IClaimDto, ILogicTypeEnum, ISaveTemplateClaimAndLogicRequestDto, ITemplateConditionNodeDto } from "../interfaces/IUtilities";
import { getAll, getById, post } from "../pages/_request";
import { LogicTypeEnumText } from "./EnumeratorHelper";
import { v4 as uuidv4 } from 'uuid';
import { ProfileType, User_info, UserDetails, UserLike } from "../constants/constants";

export const collectClaimIds = <TNode extends IBaseConditionNode<TNode>>(node: TNode): string[] => {
    if (node.logicType === ILogicTypeEnum.LEAF) return [node.claimId || ''];
    return node.children.flatMap(collectClaimIds);
};

export const buildClaimIdErrorMap = <TNode extends IBaseConditionNode<TNode>>(node: TNode, message: string): Record<string, string> => {
    const errors: Record<string, string> = {};
    const collect = (n: TNode) => {
      if (n.logicType === ILogicTypeEnum.LEAF) {
          errors[`${n.id}.claimId`] = message;
      } else {
          n.children.forEach(collect);
      }
    };
    collect(node);
    return errors;
};

export const stringifyCondition = <TNode extends IBaseConditionNode<TNode>>(node: TNode, claimMap: Record<string, string>): string => {
  if (!node) return "";

  const logicLabel = {
      [ILogicTypeEnum.AND]: LogicTypeEnumText[ILogicTypeEnum.AND],
      [ILogicTypeEnum.OR]: LogicTypeEnumText[ILogicTypeEnum.OR],
      [ILogicTypeEnum.LEAF]: '',
      [ILogicTypeEnum.ANDNOT]: LogicTypeEnumText[ILogicTypeEnum.ANDNOT],
      [ILogicTypeEnum.ORNOT]: LogicTypeEnumText[ILogicTypeEnum.ORNOT]
  };

  if (node.logicType === ILogicTypeEnum.LEAF) {
    const label =
      claimMap[node.claimId ?? ""] || node.claimId || String(i18next.t('EmptyClaim'));
    return node.negated ? `NOT ${label}` : label;
  }

  const children = (node.children || [])
    .map((child) => stringifyCondition(child, claimMap))
    .filter((s) => s.length > 0);

  if (children.length === 0) return "";

  let op = ` ${logicLabel[node.logicType]} `;
  return `(${children.join(op)})`;
};

export const getAllClaims = async () => {
  try {
    let result = await post(`/api/claims/autocomplete/?searchText=getAll`, {}) as IAxiosGetResponse;
    const respStatus = result?.response.status;

    if (respStatus === 200) {
        let data = result.data as IClaimAutocompleteResultDto[];     
        return data;
    } else {
        throw new Error(i18next.t('StatusCode', { code: String(respStatus) }));
    }
  } catch (error) {
    console.log(i18next.t('ErrorGettingAllClaims', { error: String(error) }));
    return [];
  }
};

export const mapAllClaims = (claims: IClaimAutocompleteResultDto[]): Record<string, string> => {
    const map: Record<string, string> = {};
        claims.forEach(claim => {
        map[claim.id] = claim.type; 
    });

    return map;
};

export const getTemplateClaimAndLogicData = async (templateClaimId: string) => {
    try {
        let result = await getById("/api/templateclaims/", templateClaimId);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
            let data = result.data as ISaveTemplateClaimAndLogicRequestDto;
            return data;
        } else {
            throw new Error(i18next.t('StatusCode', { code: String(respStatus) }));
        }
    } catch (error) {
        console.log(i18next.t('ErrorGettingTemplateClaimAndLogic', { error: String(error) }));
        return {};
    }
};

export const convertTemplateToClaimNode = (templateNode: ITemplateConditionNodeDto, transactionId: string): IClaimConditionNodeDto => {
  return {
    id: templateNode.id,
    transactionId,
    parentId: templateNode.parentId,
    logicType: templateNode.logicType,
    claimId: templateNode.claimId,
    orderIndex: templateNode.orderIndex,
    negated: templateNode.negated,
    customValue: templateNode.customValue,
    children: templateNode.children.map((child) => convertTemplateToClaimNode(child, transactionId))
  };
}

export const getAllClaimsWithoutFilters = async () => {
  try {
    let params = ``; 
    let results = await getAll('/api/claims/getall-nofilters', params);
    let respStatus = results?.status || results?.response.status;
    
    if (respStatus === 200) {
      let data = results?.data as IClaimDto[];     
      return data;
    } else {
      throw new Error(i18next.t('StatusCode', { code: String(respStatus) }));
    }
  } catch (error) {
    console.log(i18next.t('ErrorGettingAllClaims', { error: String(error) }));
    return [];
  }
};

export const getProfileTypeClaim = (claims: IClaimDto[]): string => {
  try {
    const scopesToMatch = [UserDetails, User_info, UserLike];
    const claimId =
      claims.find(
        claim =>
          claim.type.toLowerCase().includes(ProfileType) &&
          scopesToMatch.some(scope =>
            claim.scope.toLowerCase().includes(scope)
          )
      )?.id ?? "";

    return claimId;
  } catch (error) {
    console.log(i18next.t('ErrorGettingProfileTypeClaim', { error: String(error) }));
    return '';
  }
};

export const createRootNode = (id: string, profileTypeList: string[], profileTypeClaimId: string): IClaimConditionNodeDto => {
  var rootNode = {} as IClaimConditionNodeDto;

  if (profileTypeList.length === 1)
  {
    rootNode = {
      id: uuidv4(),
      transactionId: id,
      parentId: null,
      logicType: ILogicTypeEnum.LEAF,
      claimId: profileTypeClaimId,
      orderIndex: 1,
      negated: false,
      customValue: profileTypeList[0],
      children: []
    };
  }
  else
  {
    var rootId = uuidv4();

    rootNode = {
      id: rootId,
      transactionId: id,
      parentId: null,
      logicType: ILogicTypeEnum.OR,
      claimId: null,
      orderIndex: 1,
      negated: false,
      customValue: null,
      children: 
        profileTypeList.map((profile, idx) => ({
          id: uuidv4(),
          transactionId: id,
          parentId: rootId,
          logicType: ILogicTypeEnum.LEAF,
          claimId: profileTypeClaimId,
          orderIndex: idx + 1,
          negated: false,
          customValue: profile,
          children: []
        }))
    };
  }

  return rootNode;
};
